/**
 * VoyagesEtArticlesPage — Phase A édito (2026-09-21)
 * ---------------------------------------------------------------------
 * Page pivot back-office. Explique clairement les 2 possibilités et
 * redirige vers les moteurs EXISTANTS (aucune duplication) :
 *   A — Article simple : réutilise le moteur Article (/dashboard/articles)
 *   B — Voyage / Reportage multi-jours : réutilise Trips (/dashboard/trips)
 * Ajoute un accès direct aux Lots d'importation médias.
 * Aucune donnée ni logique de contenu ici — que du routage clair.
 */
import { Link } from "react-router-dom";
import { FileText, Ship, Images, ArrowRight, BookOpenText, Layers } from "lucide-react";

const cards = [
  {
    key: "article",
    to: "/dashboard/articles",
    icon: FileText,
    title: "Article simple",
    subtitle: "Actualité, test/avis, analyse, carnet court…",
    bullets: [
      "Réutilise EXACTEMENT le moteur Article (rédaction, médias, publication, PDF).",
      "Un article simple n'est PAS obligatoirement lié à un voyage.",
      "À tout moment il peut être RATTACHÉ à un voyage ou à une étape existante.",
    ],
    cta: "Ouvrir la liste des articles",
    dataTestId: "voyages-articles-card-article",
    accent: "border-blue-200 hover:border-blue-400 hover:bg-blue-50/40",
  },
  {
    key: "trip",
    to: "/dashboard/trips",
    icon: Ship,
    title: "Voyage / Reportage multi-jours",
    subtitle: "Conteneur éditorial : couverture, étapes, articles associés, photos.",
    bullets: [
      "Un voyage = ensemble de journées/étapes (croisière, circuit, séjour…).",
      "Chaque étape peut avoir texte, lieux, photos, notes, articles associés.",
      "Une passerelle vers les Fiches pratiques est prévue (arrivera en phase C).",
    ],
    cta: "Ouvrir la liste des voyages",
    dataTestId: "voyages-articles-card-trip",
    accent: "border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50/40",
  },
];

export default function VoyagesEtArticlesPage() {
  return (
    <div data-testid="voyages-articles-page" className="max-w-5xl">
      <header className="mb-8">
        <h1 className="font-heading text-3xl text-primary flex items-center gap-3">
          <BookOpenText className="h-7 w-7" />
          Voyages &amp; Articles
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
          Deux façons complémentaires de produire du contenu éditorial. Choisissez selon le
          format : un texte autonome, ou un reportage plus large étalé sur plusieurs jours.
          Les deux réutilisent les mêmes moteurs existants (médias, publication, PDF, Blue).
        </p>
      </header>

      <div className="grid gap-6 md:grid-cols-2 mb-10">
        {cards.map((c) => (
          <Link
            key={c.key}
            to={c.to}
            data-testid={c.dataTestId}
            className={`group block bg-white border rounded-xl p-6 transition-colors ${c.accent}`}
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="inline-flex items-center gap-2 text-primary font-heading text-xl">
                  <c.icon className="h-6 w-6" />
                  {c.title}
                </div>
                <p className="text-sm text-muted-foreground mt-1">{c.subtitle}</p>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition" />
            </div>
            <ul className="text-sm space-y-2 mb-4">
              {c.bullets.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-primary">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="text-sm font-medium text-primary group-hover:underline">{c.cta} →</div>
          </Link>
        ))}
      </div>

      <div className="bg-white border border-border rounded-xl p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-xl text-primary flex items-center gap-2">
              <Layers className="h-5 w-5" />
              Lots d'importation médias
            </h2>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              À chaque campagne d'import massif de photos, créez un lot (ex. « MSC Meraviglia — septembre 2026 »).
              Les médias importés sont regroupés : plus facile à retrouver, à répartir sur les voyages,
              étapes, articles ou fiches pratiques.
            </p>
          </div>
          <Link
            to="/dashboard/medias/lots"
            data-testid="voyages-articles-btn-batches"
            className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90"
          >
            <Images className="h-4 w-4" />
            Gérer les lots
          </Link>
        </div>
      </div>
    </div>
  );
}
